import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { ContextService } from '../services/context.service';
import { PaymentService } from '../services/payment.service';
import {
  TeamsWorkspaceMembersService,
  WorkspaceMember,
  WorkspaceMemberRequestError,
  WorkspaceMemberRole
} from './teams-workspace-members.service';

type TeamsContextState = 'loading' | 'noStorage' | 'noLicense' | 'ready' | 'adminRequired' | 'error';
type MemberSource = 'secure' | 'legacy';

@Component({
  selector: 'app-teams-members',
  templateUrl: './teams-members.component.html',
  styleUrls: ['./teams-members.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TeamsMembersComponent implements OnInit {
  public readonly contextState = signal<TeamsContextState>('loading');
  public readonly members = signal<WorkspaceMember[]>([]);
  public readonly callerRole = signal<WorkspaceMemberRole | null>(null);
  public readonly memberSource = signal<MemberSource>('secure');
  public readonly workspaceId = signal('');
  public readonly seatsUsed = signal(0);
  public readonly pooledLimit = signal<number | null>(null);
  public readonly search = signal('');
  public readonly roleFilter = signal('all');
  public readonly statusFilter = signal('all');
  public readonly errorMessage = signal('');
  public readonly inviteOpen = signal(false);
  public readonly savingInvite = signal(false);
  public readonly mutatingRowKey = signal<string | null>(null);

  public inviteEmail = '';
  public inviteRole: 'admin' | 'user' = 'user';
  public inviteOutsideCowork = false;

  public readonly filteredMembers = computed(() => {
    const search = this.search().trim().toLowerCase();
    const role = this.roleFilter();
    const status = this.statusFilter();
    return this.members().filter((member) => {
      const memberStatus = member.disabled ? 'disabled' : member.status;
      return (!search || member.email?.toLowerCase().includes(search))
        && (role === 'all' || member.role === role)
        && (status === 'all' || memberStatus === status);
    });
  });

  public readonly activeCount = computed(() => this.members().filter((member) => !member.disabled && member.status === 'active').length);
  public readonly pendingCount = computed(() => this.members().filter((member) => !member.disabled && member.status === 'pending').length);
  public readonly externalCount = computed(() => this.members().filter((member) => !member.disabled && member.external === true).length);

  constructor(
    private contextService: ContextService,
    private paymentService: PaymentService,
    private membersService: TeamsWorkspaceMembersService
  ) {}

  public async ngOnInit(): Promise<void> {
    try {
      const context = await this.contextService.load();
      if (!context?.workspace || !context.hasTeamsEntitlement) {
        this.contextState.set('noLicense');
        return;
      }
      if (!context.hasWorkspaceStorage) {
        this.contextState.set('noStorage');
        return;
      }
      this.workspaceId.set(context.workspace);
      this.callerRole.set(context.clarityUser?.['user_type'] === 'admin' ? 'admin' : 'user');
      await this.loadMembers();
    } catch (error) {
      this.handleLoadError(error);
    }
  }

  public async loadMembers(): Promise<void> {
    this.errorMessage.set('');
    try {
      const response = await this.membersService.list(this.workspaceId());
      this.memberSource.set('secure');
      this.members.set(response.members || []);
      this.callerRole.set(response.callerRole);
      this.seatsUsed.set(response.seatsUsed || 0);
      this.pooledLimit.set(response.pooledLimit ?? null);
      this.contextState.set('ready');
    } catch (error) {
      if (error instanceof WorkspaceMemberRequestError && error.status === 409 && error.code === 'not_a_pooled_plan') {
        this.loadLegacyMembers();
        return;
      }
      this.handleLoadError(error);
    }
  }

  public updateSearch(value: string): void {
    this.search.set(value);
  }

  public updateRoleFilter(value: string): void {
    this.roleFilter.set(value);
  }

  public updateStatusFilter(value: string): void {
    this.statusFilter.set(value);
  }

  public canSetRole(member: WorkspaceMember): boolean {
    if (this.memberSource() === 'legacy') {
      const adminCount = this.members().filter((candidate) => candidate.role === 'admin').length;
      return this.callerRole() === 'admin' && !(member.role === 'admin' && adminCount === 1);
    }
    return this.callerRole() === 'owner' && member.role !== 'owner' && member.external !== true && !member.disabled;
  }

  public canManageMember(member: WorkspaceMember): boolean {
    if (this.memberSource() === 'legacy') {
      return this.callerRole() === 'admin' && !(member.role === 'admin' && this.members().filter((candidate) => candidate.role === 'admin').length === 1);
    }
    if (member.role === 'owner' || member.disabled) return false;
    return this.callerRole() === 'owner' || (this.callerRole() === 'admin' && member.role === 'user');
  }

  public licenseLabel(member: WorkspaceMember): string {
    const users = this.paymentService.subscription$.value?.users || [];
    const user = users.find((candidate) => candidate.title?.toLowerCase() === member.email?.toLowerCase());
    return user?.license_teams ? 'Assigned' : member.status === 'pending' ? 'On acceptance' : 'Not assigned';
  }

  public seatLimit(): number | null {
    const [workspace] = this.paymentService.workspace() || [];
    return workspace?.seats_teams ?? null;
  }

  public canInvite(): boolean {
    return this.callerRole() === 'owner' || this.callerRole() === 'admin';
  }

  public async inviteMember(): Promise<void> {
    const email = this.inviteEmail.trim().toLowerCase();
    if (!email || this.savingInvite()) return;
    this.savingInvite.set(true);
    this.errorMessage.set('');
    try {
      if (this.memberSource() === 'legacy') {
        if (this.members().some((member) => member.email.toLowerCase() === email)) {
          throw new Error('member_exists');
        }
        const users = this.legacyUsers();
        await this.saveLegacyUsers([...users, {
          email,
          user_type: this.inviteRole,
          license_pro: null,
          license_teams: 'teams'
        }]);
      } else {
        await this.membersService.add({
          workspaceId: this.workspaceId(),
          email,
          role: this.callerRole() === 'admin' ? 'user' : this.inviteRole,
          allowedOutsideCowork: this.inviteOutsideCowork
        });
      }
      this.inviteEmail = '';
      this.inviteRole = 'user';
      this.inviteOutsideCowork = false;
      this.inviteOpen.set(false);
      if (this.memberSource() === 'secure') await this.loadMembers();
    } catch (error) {
      this.errorMessage.set(error instanceof Error && error.message === 'member_exists'
        ? 'That person is already a workspace member.'
        : this.describeError(error));
    } finally {
      this.savingInvite.set(false);
    }
  }

  public async changeRole(member: WorkspaceMember, role: string): Promise<void> {
    if (!this.canSetRole(member) || (role !== 'admin' && role !== 'user')) return;
    if (this.memberSource() === 'legacy') {
      await this.mutate(member, () => this.saveLegacyUsers(this.legacyUsers().map((user) =>
        user.email.toLowerCase() === member.email.toLowerCase() ? { ...user, user_type: role } : user
      )));
      return;
    }
    await this.mutate(member, () => this.membersService.setRole(this.workspaceId(), member.rowKey, role));
  }

  public async changeAccess(member: WorkspaceMember, allowed: boolean): Promise<void> {
    if (this.memberSource() === 'legacy' || !this.canManageMember(member)) return;
    await this.mutate(member, () => this.membersService.setAccess(this.workspaceId(), member.rowKey, allowed));
  }

  public async removeMember(member: WorkspaceMember): Promise<void> {
    if (!this.canManageMember(member)) return;
    if (!window.confirm(`Remove ${member.email} from this workspace?`)) return;
    if (this.memberSource() === 'legacy') {
      await this.mutate(member, () => this.saveLegacyUsers(this.legacyUsers().filter((user) =>
        user.email.toLowerCase() !== member.email.toLowerCase()
      )));
      return;
    }
    await this.mutate(member, () => this.membersService.remove(this.workspaceId(), member.rowKey));
  }

  public trackMember(_index: number, member: WorkspaceMember): string {
    return member.rowKey;
  }

  private async mutate(member: WorkspaceMember, operation: () => Promise<unknown>): Promise<void> {
    this.mutatingRowKey.set(member.rowKey);
    this.errorMessage.set('');
    try {
      await operation();
      if (this.memberSource() === 'secure') await this.loadMembers();
    } catch (error) {
      this.errorMessage.set(this.describeError(error));
    } finally {
      this.mutatingRowKey.set(null);
    }
  }

  private loadLegacyMembers(): void {
    const users = this.paymentService.subscription$.value?.users || [];
    this.memberSource.set('legacy');
    this.members.set(users.map((user) => ({
      rowKey: `legacy:${user.title.toLowerCase()}`,
      email: user.title,
      role: user.user_type === 'admin' ? 'admin' : 'user',
      status: user.invitation === 'invited' ? 'pending' : 'active',
      external: null,
      disabled: false,
      allowedOutsideCowork: false
    })));
    this.seatsUsed.set(users.filter((user) => !!user.license_teams).length);
    this.pooledLimit.set(null);
    this.contextState.set('ready');
  }

  private legacyUsers(): Array<{ email: string; user_type: string; license_pro?: string | null; license_teams?: string | null }> {
    return (this.paymentService.subscription$.value?.users || []).map((user) => ({
      email: user.title,
      user_type: user.user_type,
      license_pro: user.license_pro,
      license_teams: user.license_teams
    }));
  }

  private async saveLegacyUsers(users: Array<{ email: string; user_type: string; license_pro?: string | null; license_teams?: string | null }>): Promise<void> {
    const response = await this.paymentService.updateUsers(this.workspaceId(), users);
    if (response?.error) throw new Error(response.error);
    await this.paymentService.verify(false, true);
    this.loadLegacyMembers();
  }

  private handleLoadError(error: unknown): void {
    if (error instanceof WorkspaceMemberRequestError && error.status === 403) {
      this.contextState.set('adminRequired');
      return;
    }
    this.errorMessage.set(this.describeError(error));
    this.contextState.set('error');
  }

  private describeError(error: unknown): string {
    if (!(error instanceof WorkspaceMemberRequestError)) return 'Member management is temporarily unavailable.';
    const messages: Record<string, string> = {
      invalid_email: 'Enter a valid email address.',
      not_owner: 'Only the workspace owner can assign the Admin role.',
      external_cannot_be_admin: 'External members can only use the User role.',
      admin_cannot_target_privileged: 'Workspace admins cannot modify owners or other admins.',
      cannot_remove_owner: 'The workspace owner cannot be removed.',
      not_authorized: 'Administrator access is required to manage workspace members.'
    };
    return messages[error.code] || 'The member update could not be completed. Please try again.';
  }
}
